import { SENDBEACON_MECHANISM } from 'saddlebag-grappler';

import getDeviceType from '../get-device-type';
import objValues from '../object/obj-values';

import {
  EXPERIMENT_CHOKE_POINT_SCHEMA_NAME,
  EXPERIMENT_CHOKE_POINT_MINIEVENT_NAME,
} from './choke-point';
import {
  ACTION_TYPE,
  DEVICE,
  AUTOSUGGEST_TELEMETRY_SCHEMA_NAME,
  AUTOSUGGEST_MINIEVENT_NAME,
} from './constants';
import {
  HOTEL_PRICES_SCHEMA_NAME,
  HOTEL_PRICES_MINIEVENT_NAME,
} from './hotel-prices';
import {
  HOTELS_FRONTEND_SCHEMA_NAME,
  HOTELS_FRONTEND_MINIEVENT_NAME,
} from './hotels-action';
import {
  HOTEL_PRICE_SEEN_MINIEVENT_NAME,
  HOTEL_PRICE_SEEN_SCHEMA_NAME,
} from './hotels-seen';
import {
  EXPERIMENT_SMART_METRIC_MINIEVENT_NAME,
  EXPERIMENT_SMART_METRIC_SCHEMA_NAME,
} from './smart-event';

import type {
  EventProps,
  MiniEventTracker as ElementEventTrackerType,
} from '@shared/types/frontend/mini-event-tracker';
import type { ObserverClient } from '@shared/types/frontend/observer-client';
import type { OneOfMessage } from '@shared/types/frontend/utils';
import type { Device } from '@shared/types/user-context';
import type { Nullable } from '@shared/types/utils';

const GRAPPLER_EVENT = 'grappler-track';

type Props = {
  impressionId: string;
  device?: Device;
  isWebView: boolean;
  trafficSource: string;
  utmSource: Nullable<string>;
  utmMedium: Nullable<string>;
  utmCampaign: Nullable<string>;
  utmTerm: Nullable<string>;
  observerClient: ObserverClient;
  pageType: string;
};

const MiniEventTracker = ({
  device,
  impressionId,
  isWebView = false,
  observerClient,
  pageType,
  trafficSource,
  utmCampaign,
  utmMedium,
  utmSource,
  utmTerm,
}: Props): ElementEventTrackerType => {
  const miniEventActions = objValues(ACTION_TYPE);

  const trackMiniEvent = ({
    consentRequired = true,
    eventName,
    fullSchemaName,
    message,
    messageOpts,
  }: EventProps) => {
    observerClient.track(GRAPPLER_EVENT, {
      eventName,
      fullSchemaName,
      message,
      consentRequired,
      isMiniEvent: true,
      messageOpts,
    });
  };

  const trackHotelsAction = (name: string, props: OneOfMessage = {}) => {
    if (!miniEventActions.includes(name)) {
      throw new Error(
        'Actions on Element Events should be from the ACTION_TYPE object',
      );
    }

    trackMiniEvent({
      eventName: HOTELS_FRONTEND_MINIEVENT_NAME,
      fullSchemaName: HOTELS_FRONTEND_SCHEMA_NAME,
      message: {
        action_type: name,
        is_bot: device ? device.isRobot : true,
        traffic_source: trafficSource,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium,
        utm_term: utmTerm,
        device: DEVICE[getDeviceType(device, isWebView).toUpperCase()],
        impression_id: impressionId,
        ...props,
      },
    });
  };

  const trackHotelPricesAction = (props: OneOfMessage = {}) => {
    trackMiniEvent({
      eventName: HOTEL_PRICES_MINIEVENT_NAME,
      fullSchemaName: HOTEL_PRICES_SCHEMA_NAME,
      messageOpts: {
        enhance: false,
        mechanism: SENDBEACON_MECHANISM,
      },
      message: {
        traffic_source: trafficSource,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium,
        utm_term: utmTerm,
        impression_id: impressionId,
        device: DEVICE[getDeviceType(device, isWebView).toUpperCase()],
        ...props,
      },
    });
  };

  const trackHotelPriceSeen = (props: OneOfMessage = {}) => {
    trackMiniEvent({
      eventName: HOTEL_PRICE_SEEN_MINIEVENT_NAME,
      fullSchemaName: HOTEL_PRICE_SEEN_SCHEMA_NAME,
      messageOpts: {
        enhance: false,
        mechanism: SENDBEACON_MECHANISM,
      },
      message: {
        traffic_source: trafficSource,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium,
        utm_term: utmTerm,
        impression_id: impressionId,
        device: DEVICE[getDeviceType(device, isWebView).toUpperCase()],
        ...props,
      },
    });
  };

  const trackExperimentChokepoint = (experimentName: string) => {
    trackMiniEvent({
      eventName: EXPERIMENT_CHOKE_POINT_MINIEVENT_NAME,
      fullSchemaName: EXPERIMENT_CHOKE_POINT_SCHEMA_NAME,
      message: {
        name: experimentName,
      },
    });
  };

  const trackSmartMetric = (smartMetricName: string) => {
    trackMiniEvent({
      eventName: EXPERIMENT_SMART_METRIC_MINIEVENT_NAME,
      fullSchemaName: EXPERIMENT_SMART_METRIC_SCHEMA_NAME,
      message: {
        name: smartMetricName,
      },
    });
  };

  const trackAutoSuggest = (props: OneOfMessage = {}) => {
    trackMiniEvent({
      eventName: AUTOSUGGEST_MINIEVENT_NAME,
      fullSchemaName: AUTOSUGGEST_TELEMETRY_SCHEMA_NAME,
      message: {
        platform: DEVICE[getDeviceType(device, isWebView).toUpperCase()],
        page_type: pageType,
        ...props,
      },
    });
  };

  return {
    trackMiniEvent,

    trackHotelsAction,

    trackHotelPricesAction,

    trackExperimentChokepoint,

    trackSmartMetric,

    trackAutoSuggest,

    trackHotelPriceSeen,
  };
};

export default MiniEventTracker;
